import clsx from 'clsx';
import React, { useState } from 'react';

import linkIcon from 'images/referral/link_icon.svg';

import { PrimaryButton } from '@components/Button/variants';

const SHOW_COPIED_TEXT_DELAY = 2000;

interface ClipboardContentsProps {
  asTextArea: boolean;
  clipboardLink: string;
}

const ClipboardContents = ({ asTextArea, clipboardLink }: ClipboardContentsProps) => {
  return (
    <div
      className={clsx(
        'mb-24 flex items-center overflow-hidden border border-solid border-grayscale-beige px-8 py-16',
        {
          'h-auto min-h-[230px]': asTextArea,
          'h-[50px]': !asTextArea,
        }
      )}
    >
      {asTextArea ? (
        <textarea
          className="h-full min-h-[inherit] w-full resize-none overflow-hidden text-ellipsis border-none bg-white font-suisse-intl text-overline-md text-grayscale-gray"
          disabled
          value={clipboardLink}
        />
      ) : (
        <>
          <img src={linkIcon} width={15} height={15} />
          <div className="mx-4 overflow-hidden text-ellipsis whitespace-nowrap font-suisse-intl text-overline-md text-grayscale-gray">
            {clipboardLink}
          </div>
        </>
      )}
    </div>
  );
};

const LinkCopy = ({
  asTextArea,
  buttonText = 'Copy link',
  clipboardLink,
  customClass,
  successButtonText = 'Link copied!',
  trackingProps = {}, // if you want click-tracking, add properties that
  // match the EventProperties interface in "app/assets/javascripts/tracking.js"
  withInput = true,
}: {
  asTextArea?: boolean;
  buttonText?: string;
  clipboardLink: string;
  customClass?: string;
  successButtonText?: string;
  trackingProps?: { [key: string]: string };
  withInput?: boolean;
}) => {
  const [copied, setCopied] = useState<boolean>(false);

  const clickHandler = () => {
    navigator.clipboard.writeText(clipboardLink);
    setCopied(true);
    setTimeout(() => setCopied(false), SHOW_COPIED_TEXT_DELAY);
  };

  return (
    <div className={customClass}>
      {withInput && <ClipboardContents asTextArea={asTextArea} clipboardLink={clipboardLink} />}
      <div
        className={clsx('flex w-full min-w-full justify-center md:min-w-[360px]', {
          'mx-0 my-8': withInput,
        })}
      >
        <PrimaryButton
          as="button"
          onClick={clickHandler}
          className="track-click"
          fullWidth
          data-type="button"
          {...trackingProps}
        >
          {copied ? successButtonText : buttonText}
        </PrimaryButton>
      </div>
    </div>
  );
};

export default LinkCopy;
